
import { supabase } from '@/integrations/supabase/client';

// Export the calculateSmartEngagement function to fix the import errors
export const calculateSmartEngagement = (metrics: any) => {
  // This function calculates smart engagement from metrics
  // Default implementation as this wasn't originally in the file
  if (!metrics) return 0;
  
  try {
    const data = typeof metrics === 'string' ? JSON.parse(metrics) : metrics;
    
    if (data.retweets !== undefined) {
      // For Twitter
      const { retweets = 0, replies = 0, likes = 0, quotes = 0 } = data;
      return (retweets * 3) + (quotes * 2) + replies + (likes * 0.5);
    } else if (data.views !== undefined) {
      // For other platforms with views
      return data.views;
    } else {
      return 0;
    }
  } catch (e) {
    console.error("Error calculating smart engagement:", e);
    return 0;
  }
};

// Helper function to check if auto-updates are enabled for a platform
export const isAutoUpdateEnabled = async (platform: string): Promise<boolean> => {
  try {
    // Check the integration_schedules table to see if this platform has auto-updates enabled
    const { data, error } = await supabase
      .from('integration_schedules')
      .select('enabled')
      .eq('integration_type', platform.toLowerCase())
      .maybeSingle();
      
    if (error) throw error;
    
    return data?.enabled === true;
  } catch (error) {
    console.error(`Error checking auto-update status for ${platform}:`, error);
    return false;
  }
};

// Helper function to manually trigger the scheduled updates for testing
export const triggerScheduledUpdates = async (platform?: string): Promise<any> => {
  try {
    const body = platform ? { platform } : {};
    
    const { data, error } = await supabase.functions.invoke('run-scheduled-updates', {
      body
    });
    
    if (error) throw error;
    
    return {
      success: true,
      message: `Triggered scheduled updates${platform ? ` for ${platform}` : ''}`,
      data
    };
  } catch (error) {
    console.error('Error triggering scheduled updates:', error);
    throw error;
  }
};

const updateYoutubeMetrics = async (postId: string, videoLink: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('youtube-track-metrics', {
      body: { videoLink }
    });

    if (error) {
      console.error('Error invoking youtube-track-metrics function:', error);
      throw error;
    }

    if (data && data.statistics && data.statistics.viewCount) {
      const viewCount = parseInt(data.statistics.viewCount);

      // Update the post with the new view count
      const { error: updateError } = await supabase
        .from('kol_tracker')
        .update({
          content_views: viewCount,
          engagement_metrics: JSON.stringify({ views: viewCount, last_updated: new Date().toISOString() }),
          updated_date: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);

      if (updateError) {
        throw updateError;
      }

      return {
        success: true,
        message: `Updated YouTube metrics: ${viewCount} views`,
        data
      };
    } else {
      return {
        success: false,
        message: 'No view count returned from YouTube API',
        data
      };
    }
  } catch (error) {
    console.error('Error updating YouTube metrics:', error);
    throw error;
  }
};

const updateTwitterMetrics = async (postId: string, postLink: string) => {
  try {
      // Update function to use the correct parameters for the twitter-track-metrics endpoint
      const { data, error } = await supabase.functions.invoke('twitter-track-metrics', {
          body: { 
              postLink,
              mode: "post_metrics" 
          }
      });

      if (error) {
          console.error('Error invoking twitter-track-metrics function:', error);
          throw error;
      }

      // Check if we have the public metrics data
      if (data && data.data && data.data.data && data.data.data.public_metrics) {
          const { retweet_count, reply_count, like_count, quote_count, impression_count } = data.data.data.public_metrics;
          const totalEngagement = retweet_count + reply_count + like_count + quote_count;
          
          // Use impression_count as content_views for Twitter
          const contentViews = impression_count || totalEngagement;

          console.log(`Updating Twitter metrics for post ${postId}: impressions=${impression_count}, engagement=${totalEngagement}`);

          // Prepare update object
          const updateData: any = {
              content_views: contentViews, // Use impression count as content_views
              engagement_metrics: JSON.stringify({
                  retweets: retweet_count,
                  replies: reply_count,
                  likes: like_count,
                  quotes: quote_count,
                  impressions: impression_count,
                  total: totalEngagement,
                  last_updated: new Date().toISOString()
              }),
              updated_date: new Date().toISOString().split('T')[0],
              updated_at: new Date().toISOString()
          };

          // Extract and update posting_date if available from Twitter API
          if (data.data.data.created_at) {
              try {
                  const tweetCreatedAt = new Date(data.data.data.created_at);
                  // Format as YYYY-MM-DD to match the existing posting_date format
                  const year = tweetCreatedAt.getFullYear();
                  const month = String(tweetCreatedAt.getMonth() + 1).padStart(2, '0');
                  const day = String(tweetCreatedAt.getDate()).padStart(2, '0');
                  updateData.posting_date = `${year}-${month}-${day}`;
                  
                  console.log(`Updating posting_date for post ${postId} to: ${updateData.posting_date}`);
              } catch (dateError) {
                  console.error('Error parsing Twitter created_at date:', dateError);
                  // Continue without updating posting_date if date parsing fails
              }
          }

          // Update the post with the new engagement metrics and posting date
          const { error: updateError } = await supabase
              .from('kol_tracker')
              .update(updateData)
              .eq('id', postId);

          if (updateError) {
              throw updateError;
          }

          return {
              success: true,
              message: `Updated Twitter metrics: ${contentViews} views, ${totalEngagement} engagements${updateData.posting_date ? `, posting date: ${updateData.posting_date}` : ''}`,
              data
          };
      } else {
          return {
              success: false,
              message: 'No engagement data returned from Twitter API',
              data
          };
      }
  } catch (error) {
      console.error('Error updating Twitter metrics:', error);
      throw error;
  }
};

export const updatePostMetrics = async (postId: string, postLink: string, integrationEnable: boolean) => {
  try {
    if (!postLink || !integrationEnable) {
      throw new Error('Post link is empty or integration is disabled');
    }
    
    if (postLink.toLowerCase().includes('youtube.com') || postLink.toLowerCase().includes('youtu.be')) {
      return updateYoutubeMetrics(postId, postLink);
    } else if (postLink.toLowerCase().includes('twitter.com') || postLink.toLowerCase().includes('x.com')) {
      return updateTwitterMetrics(postId, postLink);
    } else if (postLink.toLowerCase().includes('t.me') || postLink.toLowerCase().includes('telegram.me')) {
      return updateTelegramMetrics(postId, postLink);
    } else {
      throw new Error(`Unsupported platform for post link: ${postLink}`);
    }
  } catch (error) {
    console.error('Error in updatePostMetrics:', error);
    throw error;
  }
};

// Add specific function for Telegram metrics
const updateTelegramMetrics = async (postId: string, postLink: string) => {
  try {
    console.log(`Updating Telegram metrics for post ${postId} with link ${postLink}`);
    
    const { data, error } = await supabase.functions.invoke('telegram-track-views', {
      body: { postLink }
    });
    
    if (error) {
      console.error('Error invoking telegram-track-views function:', error);
      throw error;
    }
    
    console.log('Telegram metrics update response:', data);
    
    if (data && data.views) {
      // Update the post with the new metrics
      const { error: updateError } = await supabase
        .from('kol_tracker')
        .update({
          content_views: data.views,
          updated_date: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);
        
      if (updateError) {
        throw updateError;
      }
      
      return {
        success: true,
        message: `Updated Telegram metrics: ${data.views} views`,
        data
      };
    } else {
      return {
        success: false,
        message: data?.message || 'No metrics data returned',
        data
      };
    }
  } catch (error) {
    console.error('Error updating Telegram metrics:', error);
    throw error;
  }
};

export const updateMultiplePostsMetrics = async (
  posts: { id: string; post_link: string; integration_enable: boolean, platform?: string }[],
  onProgress?: (current: number, total: number, url: string) => void
) => {
  const results = [];
  let current = 0;
  const total = posts.length;
  
  for (const post of posts) {
    try {
      current++;
      if (onProgress) {
        onProgress(current, total, post.post_link);
      }
      
      // Add a small delay between requests to avoid rate limiting
      if (current > 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Check if this is a valid URL before proceeding
      if (!post.post_link || !post.integration_enable) {
        continue;
      }
      
      const result = await updatePostMetrics(post.id, post.post_link, post.integration_enable);
      results.push({ id: post.id, result });
    } catch (error) {
      console.error(`Error updating post ${post.id}:`, error);
      results.push({ 
        id: post.id, 
        result: { 
          success: false, 
          message: error instanceof Error ? error.message : 'Unknown error'
        } 
      });
    }
  }
  
  return results;
};
